import { createColumnHelper } from '@tanstack/react-table';
import _ from 'lodash';
import { Edit2 } from 'lucide-react';

import { isoDateFromTimestamp } from '@/portainer/filters/filters';
import { isBE } from '@/react/portainer/feature-flags/feature-flags.service';
import { StatusType } from '@/react/edge/edge-stacks/types';
import { EdgeUpdateListItemResponse } from '@/react/portainer/environments/update-schedules/queries/list';
import { EdgeGroupLabels } from '@/react/portainer/environments/update-schedules/common/EdgeGroupLabels';

import { buildNameColumnFromObject } from '@@/datatables/buildNameColumn';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { Tooltip } from '@@/Tip/Tooltip';
import { IconButton } from '@@/buttons/IconButton';

import { StatusType as ScheduleUpdateStatus } from '../../types';

import { DeleteScheduleButton } from './DeleteScheduleButton';
import { UpdateScheduleStatus } from './UpdateScheduleStatus';
import { DeploymentCounter } from './DeploymentCounter';

const columnHelper = createColumnHelper<EdgeUpdateListItemResponse>();

export const columns = _.compact([
  columnHelper.accessor('edgeStackId', {
    header: '',
    meta: {
      className: 'w-1',
    },
    cell: ({ row: { original: item } }) => (
      <div className="flex">
        <UpdateScheduleStatus updateSchedule={item} />
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  }),
  buildNameColumnFromObject<EdgeUpdateListItemResponse>({
    nameKey: 'name',
    path: '.item',
    dataCy: 'update-schedules-name',
    idParam: 'id',
  }),
  isBE &&
    columnHelper.accessor('edgeStack.ScheduledTime', {
      header: () => (
        <div className="flex items-center gap-1">
          Scheduled
          <Tooltip message="Time is based on the Agents local timezone" />
        </div>
      ),
      cell: ({ getValue }) => getValue(), // Keep cell simple now
      enableHiding: false,
    }),
  isBE &&
    columnHelper.accessor('edgeGroupIds', {
      header: 'Edge groups',
      cell: ({ getValue }) => <EdgeGroupLabels edgeGroupIds={getValue()} />,
      enableHiding: false,
      enableSorting: true,
    }),
  isBE &&
    columnHelper.accessor('edgeStack.CreationDate', {
      header: 'Created',
      cell: ({ getValue }) => isoDateFromTimestamp(getValue()),
      enableHiding: true,
    }),
  columnHelper.accessor(
    (item) => {
      let acked = item.aggregatedStatus?.[StatusType.Acknowledged] ?? 0;
      const deploymentReceived =
        item.aggregatedStatus?.[StatusType.DeploymentReceived] ?? 0;
      if (deploymentReceived > acked) {
        acked = deploymentReceived;
      }
      const remoteUpdateSuccess =
        item.aggregatedStatus?.[StatusType.RemoteUpdateSuccess] ?? 0;
      if (remoteUpdateSuccess > acked) {
        acked = remoteUpdateSuccess;
      }

      return acked;
    },
    {
      header: 'Acknowledged',
      enableSorting: false,
      enableHiding: false,
      cell: ({ getValue, row: { original: item } }) => (
        <DeploymentCounter
          count={getValue()}
          type={StatusType.Acknowledged}
          total={item.edgeStack?.NumDeployments ?? 0}
          scheduleStatus={item.status}
        />
      ),
      meta: {
        className: '[&>*]:justify-center',
        width: '12.5%',
      },
    }
  ),
  isBE &&
    columnHelper.accessor(
      (item) => {
        let deploymentReceived =
          item.aggregatedStatus?.[StatusType.DeploymentReceived] ?? 0;
        const remoteUpdateSuccess =
          item.aggregatedStatus?.[StatusType.RemoteUpdateSuccess] ?? 0;
        if (remoteUpdateSuccess > deploymentReceived) {
          deploymentReceived = remoteUpdateSuccess;
        }

        return deploymentReceived;
      },
      {
        header: 'Started',
        cell: ({ getValue, row: { original: item } }) => (
          <DeploymentCounter
            count={getValue()}
            type={StatusType.Deploying}
            total={item.edgeStack?.NumDeployments ?? 0}
            scheduleStatus={item.status}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        meta: {
          className: '[&>*]:justify-center',
          width: '12.5%',
        },
      }
    ),
  columnHelper.accessor(
    (item) => item.aggregatedStatus?.[StatusType.RemoteUpdateSuccess] ?? 0,
    {
      header: 'Completed',
      cell: ({ getValue, row: { original: item } }) => (
        <DeploymentCounter
          count={getValue()}
          type={StatusType.RemoteUpdateSuccess}
          total={item.edgeStack?.NumDeployments ?? 0}
          scheduleStatus={item.status}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      meta: {
        className: '[&>*]:justify-center',
        width: '12.5%',
      },
    }
  ),
  columnHelper.accessor(
    (item) => item.aggregatedStatus?.[StatusType.Error] ?? 0,
    {
      header: 'Failed',
      cell: ({ getValue, row: { original: item } }) => (
        <DeploymentCounter
          count={getValue()}
          type={StatusType.Error}
          total={item.edgeStack?.NumDeployments ?? 0}
          scheduleStatus={item.status}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      meta: {
        className: '[&>*]:justify-center',
        width: '12.5%',
      },
    }
  ),
  isBE &&
    columnHelper.accessor(
      (item) =>
        item.edgeStack?.GitConfig
          ? item.edgeStack?.GitConfig.ConfigHash
          : item.edgeStack?.StackFileVersion,
      {
        header: 'Target Version',
        enableSorting: false,
        cell: ({ row: { original: item } }) => (
          <div className="text-center">{item.version}</div>
        ),
        meta: {
          className: '[&>*]:justify-center',
        },
      }
    ),
  isBE &&
    columnHelper.accessor('id', {
      header: '',
      enableSorting: false,
      cell: ({ getValue, row: { original: item } }) => (
        <div className="flex gap-3 justify-end">
          <TooltipWithChildren message="Edit schedule">
            <div>
              <IconButton
                disabled={Object.values(item.edgeStack?.Status ?? {}).some(
                  (envStatus) => envStatus.Status.length > 0
                )}
                to=".edit"
                params={{ id: getValue() }}
                icon={Edit2}
                size="medium"
                data-cy={`edit-schedule-link-${item.name}`}
              />
            </div>
          </TooltipWithChildren>
          <TooltipWithChildren message="Remove schedule">
            <div>
              <DeleteScheduleButton
                schedule={item}
                disabled={item.status === ScheduleUpdateStatus.InProgress}
              />
            </div>
          </TooltipWithChildren>
        </div>
      ),
      enableHiding: false,
    }),
]);
